import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';

// Uyum profilleri (AKM POC §6.3) — vardiya planı uyum kontrollerinin kişiye göre şiddetleri. Kiracının
// varsayılanı (ComplianceSettingsModal) DEĞİŞMEZ; profil kural ile kişiye atanır (PolicyRulesPanel,
// kind "complianceProfile"). Liste/çekmece employee-statuses/config aynası.
//
// Backend LegalFramework enum'u SAYI olarak serileşir: 0 = 4857 İş Kanunu, 1 = 657 Devlet Memurları Kanunu.
export const LEGAL_FRAMEWORK_KEYS = {
  0: 'employeeStatus.labor4857',
  1: 'employeeStatus.civilServant657',
};

export const legalFrameworkLabel = (value, t) => {
  const key = LEGAL_FRAMEWORK_KEYS[value];
  return key ? t(key) : '-';
};

export const SEVERITIES = ['error', 'warning', 'info', 'off'];

// Şiddet alanları ve backend ComplianceProfileRequest varsayılanları (yeni profil bunlarla açılır).
export const SEVERITY_FIELDS = [
  { name: 'restPeriodSeverity', labelKey: 'complianceProfile.restPeriodSeverity', defaultValue: 'warning' },
  { name: 'nightWorkSeverity', labelKey: 'complianceProfile.nightWorkSeverity', defaultValue: 'warning' },
  { name: 'nightRotationSeverity', labelKey: 'complianceProfile.nightRotationSeverity', defaultValue: 'warning' },
  { name: 'weeklyRestSeverity', labelKey: 'complianceProfile.weeklyRestSeverity', defaultValue: 'warning' },
  { name: 'minorNightSeverity', labelKey: 'complianceProfile.minorNightSeverity', defaultValue: 'error' },
  { name: 'womenNightSeverity', labelKey: 'complianceProfile.womenNightSeverity', defaultValue: 'warning' },
  { name: 'weeklyHoursSeverity', labelKey: 'complianceProfile.weeklyHoursSeverity', defaultValue: 'info' },
];

export const DEFAULT_WEEKLY_MAX_MINUTES = 2700;
export const CIVIL_SERVANT_657 = 1;

const severityOf = (v, fallback) => (SEVERITIES.includes(v) ? v : fallback);

// Form değerleri gövdeye: sayı alanları TextInput'tan string gelir; boş string int alana gönderilirse
// System.Text.Json tüm gövdeyi reddeder.
export const toComplianceProfilePayload = (formData) => {
  const fd = formData || {};
  const lf = fd.legalFramework;
  const weekly = parseInt(fd.weeklyMaxPlannedMinutes, 10);
  const payload = {
    name: (fd.name || '').trim(),
    legalFramework: lf === '' || lf === null || lf === undefined ? 0 : Number(lf),
    sectorNightExceptionEnabled: !!fd.sectorNightExceptionEnabled,
    weeklyMaxPlannedMinutes: Number.isNaN(weekly) ? DEFAULT_WEEKLY_MAX_MINUTES : weekly,
  };
  SEVERITY_FIELDS.forEach((f) => { payload[f.name] = severityOf(fd[f.name], f.defaultValue); });
  return payload;
};

// GET /defaults yanıtından forma yama: yalnız kural değerleri — ad kullanıcınındır, dokunulmaz.
export const defaultsToPatch = (d) => {
  if (!d) return undefined;
  const patch = {
    legalFramework: d.legalFramework,
    weeklyMaxPlannedMinutes: d.weeklyMaxPlannedMinutes,
    sectorNightExceptionEnabled: !!d.sectorNightExceptionEnabled,
  };
  SEVERITY_FIELDS.forEach((f) => { patch[f.name] = severityOf(d[f.name], f.defaultValue); });
  return patch;
};

// "657 Varsayılanlarını Yükle": GET /v1/shift-compliance-profiles/defaults?legalFramework=1. api.js GET'te
// reddetmez — sinyal res.data.status; başarısızsa form değişmez (undefined yama).
export const load657Defaults = async () => {
  const res = await api.get('/v1/shift-compliance-profiles/defaults', { params: { legalFramework: CIVIL_SERVANT_657 } });
  if (res?.data?.status !== true) return undefined;
  return defaultsToPatch(res.data.data);
};

const useComplianceProfileApi = (queryParams) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    // api.js GET'te REDDETMEZ: hata sinyali res.data.status (.catch ölü kod olurdu).
    api.get(`/v1/shift-compliance-profiles?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        if (res?.data?.status !== true) {
          setLoadFailed(true);
          setData([]);
          setPagination({ totalRowCount: 0, pageSize: paramsToUse.pageSize || 10, pageIndex: 0 });
          return;
        }
        setLoadFailed(false);
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      await api.post('/v1/shift-compliance-profiles', toComplianceProfilePayload(formData));
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      await api.put(`/v1/shift-compliance-profiles/${id}`, toComplianceProfilePayload(formData));
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  // Bir atama kuralında kullanılan profil 409 complianceProfile.inUse ile reddedilir (toast interceptor'da)
  // → false; tablo YENİLENMEZ, onay penceresi açık kalır. Toplu silme ucu yok.
  const _delete = useCallback(async (id) => {
    try {
      await api.delete(`/v1/shift-compliance-profiles/${id}`);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const legalFrameworkOptions = useMemo(() => [
    { id: 0, name: t('employeeStatus.labor4857') },
    { id: 1, name: t('employeeStatus.civilServant657') },
  ], [t]);

  const severityOptions = useMemo(() => [
    { id: 'error', name: t('complianceProfile.severityError') },
    { id: 'warning', name: t('complianceProfile.severityWarning') },
    { id: 'info', name: t('complianceProfile.severityInfo') },
    { id: 'off', name: t('complianceProfile.severityOff') },
  ], [t]);

  // Uç yalnız `name[like]` süzgecini ve sabit sıralamayı (Name) tanır: diğer sütunlarda filtre/sıralama
  // oku gösterilmez (sahte süzgeç olurdu).
  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'name', label: t('complianceProfile.name'), options: { filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    {
      name: 'legalFramework',
      label: t('complianceProfile.legalFramework'),
      options: { filter: false, sort: false, customBodyRender: (v) => legalFrameworkLabel(v, t) },
    },
    {
      name: 'weeklyMaxPlannedMinutes',
      label: t('complianceProfile.weeklyMaxPlannedMinutes'),
      options: { filter: false, sort: false },
    },
  ], [t]);

  const objectSchema = useMemo(() => [
    { name: 'name', label: t('complianceProfile.name'), type: 'text', grid: 12, required: true, maxLength: 100 },
    {
      name: 'legalFramework', label: t('complianceProfile.legalFramework'), type: 'select', grid: 6, required: true,
      options: legalFrameworkOptions, idKey: 'id', labelKey: 'name', defaultValue: 0,
    },
    {
      name: 'weeklyMaxPlannedMinutes', label: t('complianceProfile.weeklyMaxPlannedMinutes'), type: 'number', grid: 6,
      required: true, min: 1, max: 10080, defaultValue: DEFAULT_WEEKLY_MAX_MINUTES,
    },
    {
      name: 'loadDefaults', label: t('complianceProfile.loadDefaults'), type: 'action', grid: 12,
      onClick: () => load657Defaults(),
    },
    ...SEVERITY_FIELDS.map((f) => ({
      name: f.name, label: t(f.labelKey), type: 'select', grid: 6, required: true,
      options: severityOptions, idKey: 'id', labelKey: 'name', defaultValue: f.defaultValue,
    })),
    {
      name: 'sectorNightExceptionEnabled', label: t('complianceProfile.sectorNightException'), type: 'checkbox', grid: 12,
    },
  ], [t, legalFrameworkOptions, severityOptions]);

  return { objectSchema, columns, data, fetching, loadFailed, pagination, create, update, _delete };
};

export default useComplianceProfileApi;
